import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Workflow, WorkflowAnalytics } from "@/types"
import {
  ArrowLeft,
  Loader2,
  MousePointer,
  Send,
  Settings2,
  Users,
} from "lucide-react"

type WorkflowAnalyticsResponse = {
  workflow: Workflow
  analytics: WorkflowAnalytics
}

const statusVariant: Record<string, "default" | "secondary" | "destructive" | "outline-solid"> = {
  running: "default",
  paused: "secondary",
  completed: "secondary",
  failed: "destructive",
}

const formatDateTime = (value: string | null) => {
  if (!value) return "\u2014"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return "\u2014"
  return parsed.toLocaleString("pt-BR")
}

const formatStatus = (status: string) => {
  const map: Record<string, string> = {
    running: "em execução",
    paused: "pausado",
    completed: "concluído",
    failed: "falhou",
  }
  return map[status] ?? status
}

const pct = (ratio: number) => `${(ratio * 100).toFixed(1)}%`

const WorkflowAnalyticsPage = () => {
  const { projectSlug, workflowId } = useParams<{ projectSlug: string; workflowId: string }>()
  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows/${workflowId}`

  const analyticsQuery = useQuery<WorkflowAnalyticsResponse, ApiError>({
    queryKey: ["workflowAnalytics", projectSlug, workflowId],
    queryFn: () => apiRequest<WorkflowAnalyticsResponse>(`${basePath}/analytics`),
    enabled: Boolean(projectSlug && workflowId),
    refetchInterval: 30000,
  })

  const workflow = analyticsQuery.data?.workflow
  const analytics = analyticsQuery.data?.analytics

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/${projectSlug}/workflows`}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {workflow?.name ?? "Análises do Workflow"}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Desempenho agregado da automação de inscritos
            </p>
          </div>
        </div>
        <Button variant="outline" asChild disabled={!workflowId}>
          <Link to={`/${projectSlug}/workflows/${workflowId}/builder`}>
            <Settings2 className="mr-2 h-4 w-4" />
            Abrir Construtor
          </Link>
        </Button>
      </div>

      {analyticsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {analyticsQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            {analyticsQuery.error?.message ?? "Falha ao carregar análises do workflow."}
          </p>
        </div>
      ) : null}

      {analytics ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs text-muted-foreground">Inscrições (runs)</p>
              <p className="mt-1 text-2xl font-bold">{analytics.runs.total.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-blue-50 p-4">
              <p className="text-xs text-blue-700">Em execução</p>
              <p className="mt-1 text-2xl font-bold text-blue-800">
                {(analytics.runs.running + analytics.runs.paused).toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-xs text-green-700">Concluídas</p>
              <p className="mt-1 text-2xl font-bold text-green-800">
                {analytics.runs.completed.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border bg-red-50 p-4">
              <p className="text-xs text-red-700">Falhas</p>
              <p className="mt-1 text-2xl font-bold text-red-800">
                {analytics.runs.failed.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-4">
              <p className="text-xs text-muted-foreground">Audiência atual estimada</p>
              <p className="mt-1 text-2xl font-bold">
                {(workflow?.estimated_recipients ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs text-muted-foreground">Mensagens totais</p>
              <p className="mt-1 text-xl font-bold">{analytics.messages.total.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-blue-50 p-4">
              <p className="text-xs text-blue-700">Enviadas</p>
              <p className="mt-1 text-xl font-bold text-blue-800">{analytics.messages.sent.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-xs text-green-700">Entregues</p>
              <p className="mt-1 text-xl font-bold text-green-800">{analytics.messages.delivered.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-amber-50 p-4">
              <p className="text-xs text-amber-700">Abertas</p>
              <p className="mt-1 text-xl font-bold text-amber-800">{analytics.messages.opened.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-indigo-50 p-4">
              <p className="text-xs text-indigo-700">Cliques</p>
              <p className="mt-1 text-xl font-bold text-indigo-800">{analytics.messages.clicked.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-red-50 p-4">
              <p className="text-xs text-red-700">Falhas de envio</p>
              <p className="mt-1 text-xl font-bold text-red-800">{analytics.messages.failed.toLocaleString()}</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Send className="h-4 w-4" />
                <p className="text-xs">Taxa de entrega</p>
              </div>
              <p className="mt-1 text-2xl font-bold">{pct(analytics.rates.delivery_rate)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <p className="text-xs">Taxa de abertura</p>
              </div>
              <p className="mt-1 text-2xl font-bold">{pct(analytics.rates.open_rate)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MousePointer className="h-4 w-4" />
                <p className="text-xs">Taxa de clique</p>
              </div>
              <p className="mt-1 text-2xl font-bold">{pct(analytics.rates.click_rate)}</p>
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-sm font-semibold">Execuções Recentes</h2>
              <span className="text-xs text-muted-foreground">
                Últimas {analytics.recent_runs.length}
              </span>
            </div>

            {analytics.recent_runs.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Nenhuma execução registrada ainda.
              </div>
            ) : (
              <div className="divide-y">
                {analytics.recent_runs.map((run) => (
                  <div key={run.id} className="grid gap-3 p-4 md:grid-cols-6 md:items-center">
                    <div className="flex items-center gap-2">
                      <Badge variant={statusVariant[run.status] ?? "outline-solid"}>
                        {formatStatus(run.status)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">#{run.id}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Início:</span>{" "}
                      {formatDateTime(run.started_at)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Fim:</span>{" "}
                      {formatDateTime(run.completed_at)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Enviadas:</span>{" "}
                      {run.metrics.sent.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Entregues:</span>{" "}
                      {run.metrics.delivered.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Cliques:</span>{" "}
                      {run.metrics.clicked.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

export default WorkflowAnalyticsPage
